import React, { useEffect, useState } from 'react';
import api from './api';
import { Loader2, TrendingUp, Users, Eye, MessageSquare } from 'lucide-react';

export default function StatsPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/admin/stats').then(({ data }) => { if (alive) setData(data); });
    return () => { alive = false; };
  }, []);

  if (!data) return <div className="flex items-center gap-2 text-white/60 py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading stats…</div>;

  const max = Math.max(1, ...data.daily.map((d) => d.count));

  const cards = [
    { label: 'Total visits', value: data.total_visits, icon: TrendingUp },
    { label: 'Last 30 days', value: data.last_30_days, icon: Eye },
    { label: 'Unique visitors (30d)', value: data.unique_visitors_30d, icon: Users },
    { label: 'Unread inquiries', value: data.inquiries_unread, icon: MessageSquare },
  ];

  return (
    <div className="space-y-6" data-testid="stats-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37]">{label}</span>
              <Icon className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div className="font-serif-display text-4xl text-white" data-testid={`stat-${label}`}>{value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Daily chart */}
      <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif-display text-xl text-white">Visits — last 30 days</h3>
          <span className="text-xs text-white/55">peak {max}</span>
        </div>
        <div className="flex items-end gap-1 h-44">
          {data.daily.map((d) => (
            <div key={d.day} title={`${d.day}: ${d.count} visits`} className="flex-1 flex flex-col items-center justify-end">
              <div
                className="w-full rounded-t bg-gradient-to-t from-[#D4AF37]/40 to-[#D4AF37] transition-all hover:opacity-80"
                style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? '4px' : '0' }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-white/45 mt-2">
          <span>{data.daily[0]?.day.slice(5)}</span>
          <span>{data.daily[Math.floor(data.daily.length / 2)]?.day.slice(5)}</span>
          <span>{data.daily[data.daily.length - 1]?.day.slice(5)}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <ListCard title="Top pages" rows={data.top_pages} keyField="path" />
        <ListCard title="Languages" rows={data.top_locales} keyField="locale" />
        <ListCard title="Top referrers" rows={data.top_referrers} keyField="referrer" empty="Direct visits only" />
      </div>
    </div>
  );
}

function ListCard({ title, rows, keyField, empty = 'No data yet' }) {
  return (
    <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-6">
      <h3 className="font-serif-display text-lg text-white mb-4">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <div className="text-sm text-white/45">{empty}</div>
      ) : (
        <ul className="space-y-2.5">
          {rows.slice(0, 8).map((r, i) => (
            <li key={i} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-white/85 truncate" title={r[keyField]}>{r[keyField] || '—'}</span>
              <span className="text-[#D4AF37] font-mono text-xs shrink-0">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
